import { useEffect, useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { timeAgo } from "@/lib/format";

export default function NewsletterAdmin() {
  const [subs, setSubs] = useState({ subscribers: [], total: 0 });
  const [published, setPublished] = useState([]);
  const [selected, setSelected] = useState([]);
  const [draft, setDraft] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () =>
    api.get("/admin/newsletter/history").then((r) => setHistory(r.data?.newsletters || [])).catch(() => {});

  useEffect(() => {
    api.get("/admin/newsletter/subscribers").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/articles?status=published").then((r) => setPublished(r.data || [])).catch(() => {});
    loadHistory();
  }, []);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const generate = async () => {
    if (!selected.length) return toast.error("Select at least one story");
    const { data } = await api.post("/admin/newsletter/generate", { article_ids: selected });
    setDraft(data); setSentInfo(null); toast.success("Newsletter draft generated");
  };
  const sendNewsletter = async () => {
    if (!draft) return;
    if (!subs.total) return toast.error("No active subscribers to send to");
    if (!window.confirm(`Send this newsletter to all ${subs.total} active subscriber(s)?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/newsletter/${draft.id}/send`);
      setSentInfo(data);
      toast.success(`Sent to ${data.sent} subscriber(s)${data.failed ? `, ${data.failed} failed` : ""}`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <AdminLayout title="Newsletter — The Fin Desk Daily">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-admcard border border-admborder p-5">
          <div className="font-mono text-4xl text-cyan">{subs.total}</div>
          <div className="text-xs text-admtext2 uppercase tracking-wider mt-1">Active Subscribers</div>
          <div className="mt-4 max-h-72 overflow-auto no-scrollbar space-y-1">
            {subs.subscribers.map((s) => (
              <div key={s.id} className="text-xs text-admtext2 font-mono flex justify-between border-b border-admborder py-1.5"><span className="truncate">{s.email}</span><span className="text-admmuted">{timeAgo(s.created_at)}</span></div>
            ))}
            {!subs.subscribers.length && <div className="text-admmuted text-sm">No subscribers yet.</div>}
          </div>
        </div>

        <div className="bg-admcard border border-admborder p-5 lg:col-span-2">
          <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-3">Compose — select published stories</h3>
          <div className="max-h-72 overflow-auto no-scrollbar space-y-1 mb-4">
            {published.map((a) => (
              <label key={a.id} className="flex items-center gap-3 py-2 border-b border-admborder cursor-pointer">
                <input type="checkbox" data-testid={`newsletter-story-${a.id}`} checked={selected.includes(a.id)} onChange={() => toggle(a.id)} className="accent-cyan" />
                <span className="text-sm text-admtext truncate">{a.headline}</span>
                <span className="text-xs text-admmuted ml-auto">{a.category}</span>
              </label>
            ))}
            {!published.length && <div className="text-admmuted text-sm">No published stories to include yet.</div>}
          </div>
          <button onClick={generate} data-testid="generate-newsletter" className="bg-cyan text-admbg px-4 py-2 text-xs font-bold uppercase tracking-wider">Generate Draft</button>

          {draft && (
            <div className="mt-4 border border-admborder">
              <div className="px-4 py-2 border-b border-admborder text-xs uppercase tracking-wider text-admtext2 flex items-center justify-between">
                <span>Preview</span>
                <div className="flex items-center gap-3">
                  {sentInfo && <span className="text-emerald normal-case tracking-normal" data-testid="newsletter-sent-info">Sent to {sentInfo.sent}{sentInfo.failed ? ` · ${sentInfo.failed} failed` : ""}</span>}
                  <button onClick={sendNewsletter} disabled={sending || !subs.total} data-testid="send-newsletter" className="bg-emerald text-admbg px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
                    {sending ? "Sending…" : `Send to ${subs.total} subscriber${subs.total === 1 ? "" : "s"}`}
                  </button>
                </div>
              </div>
              <div className="bg-white max-h-96 overflow-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(draft.html) }} />
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bg-admcard border border-admborder" data-testid="newsletter-history">
        <div className="px-5 py-3 border-b border-admborder">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-admtext2">Send History</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-admmuted border-b border-admborder">
              <th className="px-5 py-2">Sent</th>
              <th className="px-3 py-2">Stories</th>
              <th className="px-3 py-2 text-right">Recipients</th>
              <th className="px-3 py-2 text-right">Failed</th>
            </tr>
          </thead>
          <tbody>
            {history.map((n) => (
              <tr key={n.id} className="border-b border-admborder last:border-0" data-testid={`newsletter-history-${n.id}`}>
                <td className="px-5 py-2.5 text-admtext font-mono">{n.sent_at ? new Date(n.sent_at).toLocaleString("en-GB") : "—"}</td>
                <td className="px-3 py-2.5 text-admtext2 truncate max-w-md">{(n.items || []).map((i) => i.headline).join(", ") || "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono text-emerald">{n.recipient_count ?? 0}</td>
                <td className="px-3 py-2.5 text-right font-mono text-crimson">{n.failed_count ?? 0}</td>
              </tr>
            ))}
            {!history.length && <tr><td colSpan={4} className="px-5 py-6 text-admmuted text-sm">No newsletters sent yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
